import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

export interface ShellMembership {
  org_id: string;
  org_name: string;
  /** `memberships.function_role_id` — null for the "Custom" role. Handed to
   *  the Agent harness so the backend can assemble the Layer-2 role rule. */
  function_role_id: string | null;
  function_role_name: string | null;
  /** Membership creation timestamp. Used to pick the user's "home org"
   *  (oldest = default) on session start. */
  created_at: string;
  /** Last-active timestamp used by `useOrgSwitcherVM` to surface
   *  recently-active orgs first. Column not yet on the schema (see follow-up
   *  in `useOrgSwitcherVM`) — extracted defensively so the VM can degrade to
   *  alphabetical ordering until the column lands. */
  last_active_at?: string | null;
  /** `memberships.suspended_at` — non-null when the user is suspended in this
   *  org (typically after a downgrade overage or a dunning cascade). Drives
   *  the "Suspended" pill in `IdentityFlyout` and the `/paywall` route
   *  override in `SignedInShell`. Consumers wired in later billing clusters. */
  suspended_at: string | null;
  /** Capability keys granted by the membership's permission group (e.g.
   *  `'billing.manage'`). Sourced from `permission_groups.capability_keys`
   *  via the join in `loadForUser`. Empty array when the row is missing the
   *  embed (defensive; shouldn't happen with seeded permission groups). Read
   *  by `useCapability(orgId, key)`. */
  capability_keys: ReadonlyArray<string>;
}

export interface ShellProfile {
  id: string;
  display_name: string;
  username: string;
  discriminator: number;
  active_surface: ShellSurfaceKey | null;
}

export type ShellSurfaceKey =
  | 'dashboard'
  | 'projects'
  | 'agent'
  | 'documents'
  | 'work-items'
  | 'members'
  | 'settings'
  | 'todos';

export type ShellStatus = 'loading' | 'authed' | 'unauth';

export interface ShellSession {
  status: ShellStatus;
  userId: string | null;
  email: string | null;
  profile: ShellProfile | null;
  memberships: ReadonlyArray<ShellMembership>;
  /** Reload memberships + profile (e.g. after switching orgs). */
  refresh: () => Promise<void>;
}

const RESOLVING_MIN_MS = 200;

/**
 * Loads the signed-in user, their profile, and their memberships. Surfaces a
 * single `status` (loading / authed / unauth) so the shell can render a
 * skeleton first-paint then resolve.
 *
 * On auth state changes (sign-out from another tab, token expiry mid-session)
 * status flips to `unauth` and the shell host can navigate to `/sign-in`.
 */
export function useShellSession(): ShellSession {
  const [status, setStatus] = useState<ShellStatus>('loading');
  const [userId, setUserId] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [profile, setProfile] = useState<ShellProfile | null>(null);
  const [memberships, setMemberships] = useState<ShellMembership[]>([]);

  async function loadForUser(uid: string) {
    const [{ data: profileRow }, { data: membershipRows }] = await Promise.all([
      supabase
        .from('profiles')
        .select('id, display_name, username, discriminator, active_surface')
        .eq('id', uid)
        .maybeSingle(),
      supabase
        .from('memberships')
        .select(
          'org_id, created_at, suspended_at, function_role_id, orgs:org_id(name), function_role:function_role_id(label), permission_group:permission_role_id(capability_keys)'
        )
        .eq('user_id', uid)
        .order('created_at', { ascending: true })
    ]);

    if (profileRow) {
      setProfile({
        id: profileRow.id,
        display_name: profileRow.display_name,
        username: profileRow.username,
        discriminator: profileRow.discriminator,
        active_surface: (profileRow.active_surface ?? null) as ShellSurfaceKey | null
      });
    } else {
      setProfile(null);
    }

    type OrgEmbed = { name?: string } | { name?: string }[] | null;
    type RoleEmbed = { label?: string } | { label?: string }[] | null;
    type PermissionGroupEmbed =
      | { capability_keys?: string[] | null }
      | { capability_keys?: string[] | null }[]
      | null;
    const rows = (membershipRows ?? []) as ReadonlyArray<{
      org_id: string;
      created_at: string;
      suspended_at?: string | null;
      last_active_at?: string | null;
      function_role_id?: string | null;
      orgs: OrgEmbed;
      function_role: RoleEmbed;
      permission_group: PermissionGroupEmbed;
    }>;
    setMemberships(
      rows.map((r) => {
        const orgName = Array.isArray(r.orgs)
          ? r.orgs[0]?.name ?? '—'
          : r.orgs?.name ?? '—';
        const roleLabel = Array.isArray(r.function_role)
          ? r.function_role[0]?.label ?? null
          : r.function_role?.label ?? null;
        const capabilityKeys = Array.isArray(r.permission_group)
          ? r.permission_group[0]?.capability_keys ?? []
          : r.permission_group?.capability_keys ?? [];
        return {
          org_id: r.org_id,
          org_name: orgName,
          function_role_id: r.function_role_id ?? null,
          function_role_name: roleLabel,
          created_at: r.created_at,
          last_active_at: r.last_active_at ?? null,
          suspended_at: r.suspended_at ?? null,
          capability_keys: capabilityKeys ?? []
        };
      })
    );
  }

  useEffect(() => {
    let cancelled = false;
    const startedAt = Date.now();

    async function init() {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      const session = data.session;
      if (!session) {
        const wait = RESOLVING_MIN_MS - (Date.now() - startedAt);
        if (wait > 0) await new Promise((r) => setTimeout(r, wait));
        if (cancelled) return;
        setStatus('unauth');
        return;
      }
      setUserId(session.user.id);
      setEmail(session.user.email ?? null);
      await loadForUser(session.user.id);
      if (cancelled) return;
      const wait = RESOLVING_MIN_MS - (Date.now() - startedAt);
      if (wait > 0) await new Promise((r) => setTimeout(r, wait));
      if (cancelled) return;
      setStatus('authed');
    }

    init();

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if (event === 'SIGNED_OUT' || !session) {
        setUserId(null);
        setEmail(null);
        setProfile(null);
        setMemberships([]);
        setStatus('unauth');
        return;
      }
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        setUserId(session.user.id);
        setEmail(session.user.email ?? null);
        loadForUser(session.user.id).then(() => {
          if (!cancelled) setStatus('authed');
        });
      }
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  return {
    status,
    userId,
    email,
    profile,
    memberships,
    async refresh() {
      if (!userId) return;
      await loadForUser(userId);
    }
  };
}
